// interface CategorizationRequest {
//   transactions: string[];
// }

interface CategorizationResponse {
  categorizations: {
    transaction: string;
    category: string;
    confidence: number;
  }[];
}

interface DocumentProcessingResponse {
  transactions: {
    amount: number;
    type: 'income' | 'expense';
    category: string;
    description: string;
    date: string;
    classification: 'personal' | 'business';
    confidence: number;
  }[];
}

interface PropertyAssignmentResponse {
  property_suggestions: {
    transaction_description: string;
    suggested_property_id?: string;
    confidence: number;
    reasoning: string;
  }[];
}

export const categorizeTransactions = async (
  transactions: string[]
): Promise<CategorizationResponse> => {
  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: `You are a personal finance assistant. Your job is to categorize transactions for a financial app into clear categories.

Please categorize each transaction into one of these categories:
- Food & Dining (groceries, restaurants, coffee shops)
- Transportation (gas, public transit, rideshare, car maintenance)
- Housing (rent, mortgage, utilities, home improvement)
- Income (salary, freelance, investment returns, gifts)
- Shopping (clothing, electronics, household items)
- Entertainment (movies, concerts, hobbies, streaming services)
- Healthcare (medical expenses, prescriptions, insurance)
- Travel (flights, hotels, vacation expenses)
- Education (tuition, books, courses, student loans)
- Business (work-related expenses, office supplies)
- Utilities (electricity, water, internet, phone)
- Insurance (health, car, home, life insurance)
- Other (anything that doesn't fit the above categories)

Respond with a JSON object in this exact format:
{
  "categorizations": [
    {
      "transaction": "original transaction text",
      "category": "categorized category",
      "confidence": 0.95
    }
  ]
}

Be confident in your categorizations and provide confidence scores between 0.8 and 1.0.`
          },
          {
            role: 'user',
            content: `Categorize the following transactions:\n${transactions.map((t, i) => `${i + 1}. ${t}`).join('\n')}`
          }
        ],
        temperature: 0.1,
        max_tokens: 1000,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No response content from OpenAI');
    }

    try {
      return JSON.parse(content);
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', content);
      throw new Error('Invalid response format from OpenAI');
    }
  } catch (error) {
    console.error('Error categorizing transactions:', error);
    throw error;
  }
};

export const processDocumentTransactions = async (
  documentText: string,
  availableProperties: Array<{ id: string; address: string; type?: string }>
): Promise<DocumentProcessingResponse> => {
  try {
    const propertiesList = availableProperties.map(p => `${p.id}: ${p.address}`).join('\n');
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: `You are a financial document processor. Extract transactions from bank statements, CSV files, or other financial documents.

For each transaction found, determine:
1. Amount (positive for income, negative for expenses)
2. Type (income or expense)
3. Category (from the predefined list)
4. Description (clear, concise description)
5. Date (in YYYY-MM-DD format)
6. Classification (personal or business)
7. Confidence score (0.8-1.0)

Categories: Food & Dining, Transportation, Housing, Income, Shopping, Entertainment, Healthcare, Travel, Education, Business, Utilities, Insurance, Other

Available properties for business transactions:
${propertiesList}

Business transactions should be related to property management, real estate, or business operations.

Respond with a JSON object in this exact format:
{
  "transactions": [
    {
      "amount": 150.00,
      "type": "expense",
      "category": "Utilities",
      "description": "Electricity bill",
      "date": "2024-01-15",
      "classification": "business",
      "confidence": 0.95
    }
  ]
}`
          },
          {
            role: 'user',
            content: `Extract transactions from this document:\n\n${documentText}`
          }
        ],
        temperature: 0.1,
        max_tokens: 2000,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No response content from OpenAI');
    }

    try {
      return JSON.parse(content);
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', content);
      throw new Error('Invalid response format from OpenAI');
    }
  } catch (error) {
    console.error('Error processing document:', error);
    throw error;
  }
};

export const assignTransactionsToProperties = async (
  transactions: Array<{ description: string; category: string; classification: string }>,
  availableProperties: Array<{ id: string; address: string; type?: string }>
): Promise<PropertyAssignmentResponse> => {
  try {
    const propertiesList = availableProperties.map(p => `${p.id}: ${p.address}`).join('\n');
    const transactionsList = transactions.map(t => `${t.description} (${t.category}, ${t.classification})`).join('\n');
    
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: `You are a property management assistant. Assign business transactions to the appropriate property based on the transaction description and category.

Available properties:
${propertiesList}

Only assign business transactions to properties. Personal transactions should have null property_id.
Consider:
- Property-related expenses (utilities, taxes, insurance, maintenance)
- Property addresses mentioned in transactions
- Transaction categories that relate to real estate

Respond with a JSON object in this exact format:
{
  "property_suggestions": [
    {
      "transaction_description": "original description",
      "suggested_property_id": "property-id-or-null",
      "confidence": 0.95,
      "reasoning": "Brief explanation of the assignment"
    }
  ]
}`
          },
          {
            role: 'user',
            content: `Assign these transactions to properties:\n\n${transactionsList}`
          }
        ],
        temperature: 0.1,
        max_tokens: 1500,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No response content from OpenAI');
    }

    try {
      return JSON.parse(content);
    } catch (parseError) {
      console.error('Failed to parse OpenAI response:', content);
      throw new Error('Invalid response format from OpenAI');
    }
  } catch (error) {
    console.error('Error assigning transactions to properties:', error);
    throw error;
  }
};

export const suggestCategory = async (description: string): Promise<string> => {
  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: `You are a personal finance assistant. Categorize the given transaction description into one of these categories:

- Food & Dining
- Transportation  
- Housing
- Income
- Shopping
- Entertainment
- Healthcare
- Travel
- Education
- Business
- Utilities
- Insurance
- Other

Respond with only the category name, nothing else.`
          },
          {
            role: 'user',
            content: `Categorize this transaction: ${description}`
          }
        ],
        temperature: 0.1,
        max_tokens: 50,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0]?.message?.content?.trim() || 'Other';
  } catch (error) {
    console.error('Error suggesting category:', error);
    return 'Other';
  }
}; 